/**
 * <product-addons>
 *
 * Toggle-based paid add-on block. Each card is one add-on variant (configured via the
 * `custom.addon_products` metafield). Turning a card on adds that variant to the SAME
 * /cart/add.js call as the main product — as a plain, independent line item (no bundle
 * properties), so it works on one-time AND subscription purchases and is editable in cart.
 *
 * This element does NOT inject hidden inputs. It exposes getSelectedItems() and the
 * submit owner (subscription-selector.js) pulls the toggled add-ons at submit time — the
 * same pull model it already uses for the Recharge selection. Impossible to desync.
 *
 * Variant-scoped visibility: a card with a parent-variant id (from `custom.addon_parent_map`)
 * only shows when that main-product variant is selected; others are always shown. Hidden cards
 * are forced off so they can never be submitted. Reacts to the theme's optionValueSelectionChange
 * pubsub event, with a light poll on the form's [name="id"] as a backstop.
 */
if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      connectedCallback() {
        this.productId = this.dataset.productId;
        this.cards = [...this.querySelectorAll('[data-addon-card]')];

        try {
          this.parentMap = JSON.parse(this.querySelector('[data-addon-parent-map]')?.textContent || '{}');
        } catch (e) {
          this.parentMap = {};
        }

        this.applyVisibility();

        // React to variant changes via the theme pubsub…
        const pubsub = window.FoxTheme && window.FoxTheme.pubsub;
        if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.optionValueSelectionChange) {
          this._unsub = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, () =>
            setTimeout(() => this.applyVisibility(), 0)
          );
        }
        // …plus a backstop poll on the selected variant id.
        this._lastId = this.currentVariantId();
        this._timer = setInterval(() => {
          const id = this.currentVariantId();
          if (id !== this._lastId) {
            this._lastId = id;
            this.applyVisibility();
          }
        }, 400);
      }

      disconnectedCallback() {
        clearInterval(this._timer);
        if (typeof this._unsub === 'function') this._unsub();
      }

      /** Current main-product variant id from its form. */
      currentVariantId() {
        const input =
          document.querySelector(`form[data-product-id="${this.productId}"] [name="id"]`) ||
          document.querySelector('form[is="product-form"] [name="id"]') ||
          document.querySelector('[name="id"]');
        return input ? String(input.value) : null;
      }

      /** Show cards whose parent-variant matches the selection (or that have no parent). */
      applyVisibility() {
        const variantId = this.currentVariantId();
        this.cards.forEach((card) => {
          const parent = this.parentMap[card.dataset.variantId];
          const visible = !parent || String(parent) === String(variantId);
          card.hidden = !visible;
          // A hidden card must never be submittable — force its toggle off.
          if (!visible) {
            const toggle = card.querySelector('[data-addon-toggle]');
            if (toggle) toggle.checked = false;
          }
        });
      }

      /** Line items for cards that are visible AND toggled on: [{ id, quantity, selling_plan? }]. */
      getSelectedItems() {
        // The whole block hides on one-time (data-hide-on-onetime → el.hidden, via
        // subscription-visibility.js). A toggle left on from a prior subscription selection
        // must never submit while the block is hidden, so short-circuit here.
        if (this.hidden) return [];
        return this.cards
          .filter((card) => !card.hidden && card.querySelector('[data-addon-toggle]')?.checked)
          .map((card) => {
            const item = { id: card.dataset.variantId, quantity: 1 };
            if (card.dataset.sellingPlanId) item.selling_plan = card.dataset.sellingPlanId;
            return item;
          });
      }
    }
  );
}
